import * as Sentry from "@sentry/nextjs";
import { toast } from "@/app/_providers/toast";
import { env } from "@/env";
import { captureEvent } from "../_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";

const SERVER_URL = env.NEXT_PUBLIC_XI_SERVER_URL;

export interface TtsfxGeneration {
  sound_generation_history_item?: {
    generation_config: {
      generation_settings: {
        duration_seconds: number;
      };
    };
    sound_generation_history_item_id?: string;
  };
  waveform_base_64: string;
  /** Optional direct audio URL (used in Storybook mocks to skip blob decoding). */
  audio_url?: string;
}

export const generateTtsfx = async ({
  config,
  token,
  source,
  showErrors = true,
  signal,
}: {
  config: { loop?: boolean; text: string };
  source: string;
  token?: string;
  showErrors?: boolean;
  signal?: AbortSignal;
}): Promise<TtsfxGeneration[]> => {
  try {
    const requestBody = {
      text: config.text,
      loop: !!config.loop,
      output_format: "opus_48000_128",
    };

    const url = `${SERVER_URL}/sound-generation`;

    captureEvent(EventName.mw_sfx_generation_started, { source, isLooping: config.loop });

    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(requestBody),
      signal,
    });

    if (res.ok && res.body !== null) {
      const generations = await res.json();
      const results = generations.sound_generations_with_waveforms;

      captureEvent(EventName.mw_sfx_generation_successful, {
        generationCount: results.length,
        source,
        isLooping: config.loop,
      });

      return results;
    } else {
      const { detail } = await res.json();
      throw detail;
    }
  } catch (e: unknown) {
    if (e instanceof Error && e.name === "AbortError") {
      throw e;
    }

    const knownApiStatuses = ["quota_exceeded", "system_busy"];
    const errorStatus = typeof e === "object" && e !== null && "status" in e ? (e as { status: string }).status : null;

    if (errorStatus !== null && knownApiStatuses.includes(errorStatus)) {
      if (showErrors) {
        toast.error(errorMap[errorStatus].title, { description: errorMap[errorStatus].description });
      }
    } else {
      if (showErrors) {
        toast.error("Something went wrong", {
          description: "It looks like there was an issue while generating. Please try again later.",
        });
      }
      Sentry.captureException(e instanceof Error ? e : new Error(String(e)), {
        extra: {
          error_status: errorStatus ?? "unknown",
          text: config.text,
        },
      });
    }

    if (errorStatus === "quota_exceeded") {
      captureEvent(EventName.mw_sfx_generation_quota_exceeded, { source, isLooping: config.loop });
    } else {
      captureEvent(EventName.mw_sfx_generation_failed, {
        error: e instanceof Error ? e.message : "Unknown error",
        source,
        isLooping: config.loop,
      });
    }

    throw e;
  }
};

const errorMap: { [key: string]: { description: string; title: string } } = {
  quota_exceeded: {
    title: "Quota Exceeded",
    description:
      "You have reached the limit of available requests on the landing page. You can continue, for free, by signing-up to the platform.",
  },
  system_busy: {
    title: "System Busy",
    description: "The system is currently experiencing unusually high levels of activity. Please try again later.",
  },
};
